<script setup lang="ts">
/**
 * This component takes arbitrary untrusted HTML, and renders it in a completely safe and highly customizable way
 */
import { computed, h, onMounted, ref } from 'vue';
import Checkbox from 'primevue/checkbox';
import { useStringFunctions, useUtilities } from '@apparatix/composables';
import { Toolbar } from './core';
import RadioFor from './RadioFor.vue';
import ConditionalImage from './ConditionalImage.vue';

type ElementName = keyof HTMLElementTagNameMap;

type TreeNode = {
  elementName: string,
  children?: Array<TreeNode | string>,
  props?: Record<string, any>,
  skipRender?: boolean,
};

type Props = {
  unsafeHtml: string,
  allowedElements?: ElementName[],
  anchorTarget?: '_self' | '_blank',
  containerClasses?: string[],
  showImageLoader?: boolean,
  loadImages?: boolean,
  isLoadingImages?: boolean,
  applyClasses?: Partial<Record<ElementName, string[]>>,
  convertTextOnlyDivsToP?: boolean,
  /**
   * When true, empty div, p, span, etc elements will be discarded
   */
  discardEmptyElements?: boolean,
};

type Emits = {
  (e: 'load-images'): void,
  (e: 'update:loadImages', loadImages: boolean): void,
};

const TEXT_COLOR = 'text-[var(--p-text-color)]';
const CONTAINER_ID = 'unsafe-html-content-wrapper';
const VOID_ELEMENTS = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr']);

// These elements would be potentially dangerous and/or cause issues with how this component works
// so we make sure to block these even if supplied as allowed in the props
const NEVER_ALLOWED_ELEMENTS = new Set(['body', 'head', 'html', 'iframe', 'meta', 'script', 'style']);

const props = withDefaults(defineProps<Props>(), {
  allowedElements: () => [
    'a', 'b', 'br', 'div', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img',
    'li', 'ol', 'p', 'span', 'strong', 'table', 'tbody', 'td',
    'tfoot', 'th', 'thead', 'tr', 'ul',
  ],
  anchorTarget: '_blank',
  discardEmptyElements: true,
  convertTextOnlyDivsToP: true,
  containerClasses: () => ['flex', 'flex-col', 'gap-4'],
  applyClasses: () => ({
    a: ['text-sm', 'lg:text-base', 'font-normal', 'no-underline', 'hover:underline', 'text-[var(--p-primary-color)]'],
    b: ['font-semibold'],
    div: ['block'],
    em: ['italic'],
    h1: ['text-2xl', 'font-bold', TEXT_COLOR],
    h2: ['text-xl', 'font-bold', TEXT_COLOR],
    h3: ['text-lg', 'font-semibold', TEXT_COLOR],
    h4: ['text-lg', 'font-medium', TEXT_COLOR],
    h5: ['text-base', 'font-medium', TEXT_COLOR],
    h6: ['text-base', 'font-medium', TEXT_COLOR],
    i: ['italic'],
    li: ['text-sm', 'lg:text-base', 'font-normal', TEXT_COLOR],
    ol: ['list-decimal'],
    p: ['text-sm', 'lg:text-base', 'font-normal'],
    strong: ['font-semibold'],
    ul: ['list-disc'],
  }),
});
const emit = defineEmits<Emits>();

const { isEmptyString } = useStringFunctions();
const { arrayHasValues, isObject } = useUtilities();

const internalLoadImages = ref(false);
const elementTree = ref<TreeNode>();
const hasAnyImages = ref(false);
const hasDeferredCidImages = ref(false);

const mergedAllowedElements = computed<ElementName[]>(() => props.allowedElements.filter(e => !NEVER_ALLOWED_ELEMENTS.has(e)));

const shouldShowImageLoader = computed(() => props.showImageLoader && mergedAllowedElements.value.includes('img') && hasAnyImages.value);

const loadImages = computed({
  get() {
    return props.loadImages ?? internalLoadImages.value;
  },
  set(shouldLoadImages) {
    internalLoadImages.value = shouldLoadImages;
    emit('update:loadImages', shouldLoadImages);

    if (shouldLoadImages && hasDeferredCidImages.value) {
      emit('load-images');
    }
  },
});

/**
 * This function is NOT intended to sanitize the untrusted html or make it safe to render.
 * The html we want to render could be anything from a short snippet to an entire document.
 * We want to remove any <html> and <head> elements the html contains, and then wrap it in
 * our own html document so the parser always receives a full doc with our container div.
 */
const standardizeHtml = (rawHtml: string) => rawHtml
  .replace(/\r?\n+/g, '')                       // Newlines
  .replace(/<!doctype[^>]*>/gi, '')             // <!DOCTYPE html>
  .replace(/<\/?html[^>]*>/gi, '')              // <html> and </html> tags only, not content between
  .replace(/<head[^>]?>[\s\S]*<\/head>/gi, '')  // everything between <head> and </head> inclusive
  .replace(/<\/?body[^>]*>/gi, '')              // <body> and </body> tags only, not content between
  .replace(/<!--[\s\S]*?-->/gi, '');            // everything between <!-- and --> inclusive

const html = (strings: TemplateStringsArray, ...values: string[]) => String.raw({ raw: strings }, ...values);

const buildDocument = (bodyContent: string) => html`<!DOCTYPE html>
  <html>
    <head>
      <meta charset="UTF-8">
    </head>
    <body>
      <div id="${CONTAINER_ID}">
        ${bodyContent}
      </div>
    </body>
  </html>
`;

const addAriaProps = (nodeProps: Record<string, any>, el: HTMLElement) => {
  const ariaAttributes = el.getAttributeNames().filter(prop => prop.startsWith('aria'));

  for (const attribute of ariaAttributes) {
    const value = el.getAttribute(attribute);

    if (isEmptyString(value, true)) continue;

    nodeProps[attribute] = value;
  }
};

const nodeIsAnchor = (node: Node): node is HTMLAnchorElement => node?.nodeName?.toLowerCase() === 'a';
const nodeIsDiv = (node: Node): node is HTMLDivElement => node?.nodeName?.toLowerCase() === 'div';
const nodeIsImage = (node: Node): node is HTMLImageElement => node?.nodeName?.toLowerCase() === 'img';

const buildNode = (domNode: Node): string | TreeNode => {
  if (domNode.nodeType === Node.TEXT_NODE) {
    return domNode.textContent;
  }

  const name = domNode.nodeName.toLowerCase();
  const hasChildren = domNode.hasChildNodes();

  const skipThisNode: TreeNode = { elementName: 'UNUSED', skipRender: true };

  if (!hasChildren && !VOID_ELEMENTS.has(name) && props.discardEmptyElements) {
    return skipThisNode;
  }

  const treeNode: TreeNode = {
    elementName: name,
    props: {},
  };

  if (nodeIsAnchor(domNode)) {
    const { href } = domNode;
    const isSupportedAnchorHref = href.startsWith('https://') || href.startsWith('http://') || href.startsWith('mailto:');

    if (isSupportedAnchorHref) {
      treeNode.props.href = href;
      treeNode.props.target = props.anchorTarget;

      if (props.anchorTarget === '_blank') {
        treeNode.props.rel = 'noopener noreferrer';
      }
    }
  }

  if (nodeIsImage(domNode)) {
    const { src } = domNode;
    const isSupportedImageSrc = src.startsWith('https://') || src.startsWith('http://') || src.startsWith('data:image');
    const isDeferredCidImage = src.toLowerCase().startsWith('cid:');

    if (isDeferredCidImage) {
      hasAnyImages.value = true;
      hasDeferredCidImages.value = true;

      return skipThisNode;
    }

    if (isEmptyString(src, true) || !isSupportedImageSrc) {
      return skipThisNode;
    }

    hasAnyImages.value = true;
    treeNode.props.src = src;

    if (!isEmptyString(domNode.alt, true)) {
      treeNode.props.alt = domNode.alt;
    }

    if (domNode.width) {
      treeNode.props.width = domNode.width;
    }

    if (domNode.height) {
      treeNode.props.height = domNode.height;
    }
  }

  if (nodeIsDiv(domNode) && domNode.id === CONTAINER_ID && arrayHasValues(props.containerClasses)) {
    treeNode.props.class = props.containerClasses;
  } else if (isObject(props.applyClasses) && arrayHasValues(props.applyClasses[name])) {
    treeNode.props.class = props.applyClasses[name];
  }

  addAriaProps(treeNode.props, domNode as HTMLElement);

  if (hasChildren) {
    const children = Array.from([...domNode.childNodes]).filter(child => child.nodeType === Node.TEXT_NODE || mergedAllowedElements.value.includes(child.nodeName.toLowerCase() as ElementName));
    if (arrayHasValues(children)) {
      treeNode.children = children.map(child => buildNode(child));
    }
  }

  return treeNode;
};

const buildTree = (domNode: Node) => {
  const name = domNode.nodeName.toLowerCase();

  if (!mergedAllowedElements.value.includes(name as ElementName)) {
    return;
  }

  elementTree.value = buildNode(domNode) as TreeNode;
};

const shouldSkipRender = (node: TreeNode | string): boolean => {
  if (typeof node === 'string') {
    return isEmptyString(node, true);
  }

  if (node.skipRender) {
    return true;
  }

  if (!props.discardEmptyElements) {
    return false;
  }

  if (node.children?.every(child => typeof child !== 'string' && child.skipRender)) {
    return true;
  }

  if (!node.children?.length) {
    return false;
  }

  let shouldSkip = true;

  node.children.forEach(child => {
    if (!shouldSkipRender(child)) {
      shouldSkip = false;
      return;
    }
  });

  return shouldSkip;
};

const renderTreeNode = (node: TreeNode) => {
  if (shouldSkipRender(node)) {
    return undefined;
  }

  if (node.elementName === 'img') {
    return h(ConditionalImage, { ...(node.props as { src: string }), shouldShow: loadImages.value });
  }

  if (arrayHasValues(node.children)) {
    return h(node.elementName, node.props, node.children.map(child => typeof child === 'string' ? child : renderTreeNode(child)));
  }

  return h(node.elementName, node.props);
};

const treeRenderer = () => {
  if (!elementTree.value) {
    return undefined;
  }

  return () => renderTreeNode(elementTree.value);
};

onMounted(() => {
  // DOMParser builds an inert document: it never executes <script> content and — unlike
  // a live iframe srcdoc — never fetches subresources, so remote images (tracking pixels)
  // in the untrusted html are not requested until the user opts in via "Load images".
  const parsedDocument = new DOMParser().parseFromString(buildDocument(standardizeHtml(props.unsafeHtml)), 'text/html');
  const container = parsedDocument.getElementById(CONTAINER_ID);

  if (!container) {
    return;
  }

  buildTree(container);
});
</script>
<template>
  <div class="flex flex-col gap-4">
    <Toolbar v-if="shouldShowImageLoader" align="left">
      <RadioFor label="Load images">
        <template #default="{id}">
          <Checkbox
            v-model="loadImages"
            :input-id="id"
            :disabled="props.isLoadingImages"
            binary
          />
        </template>
      </RadioFor>
    </Toolbar>
    <component :is="treeRenderer()" />
  </div>
</template>
